import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, lastValueFrom, map, of, Subject, take, timeout } from 'rxjs';
import domtoimage from 'dom-to-image';
import { logger } from '@logger';
import { autoQueue } from '../tools/decorators/queue-method';
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
interface Type$1<T> extends Function {
  new (...args: any[]): T;
}
export interface ViewReady {
  success: boolean;
}
const RENDER_TIMEOUT = 30000; // 30 seconds
@Injectable({
  providedIn: 'root',
})
export class RenderComponentToImageService {
  portal = new BehaviorSubject<
    { portal: ComponentPortal<unknown>; size?: { width: number; height: number } } | undefined
  >(undefined);
  attachedComponent = new Subject<unknown>();
  constructor() {}
  @autoQueue('renderComponentToImage')
  async render<C extends { viewReady: BehaviorSubject<ViewReady | undefined> }>(
    componentType: Type$1<C>,
    size?: { width: number; height: number }, // undefined 是 vw和 vh
    initialize?: (component: C) => void,
    domSelector?: string
  ): Promise<string | undefined> {
    try {
      const componentPortal = new ComponentPortal(componentType);
      this.portal.next({ portal: componentPortal, size: size });
      const attachedComponent = await lastValueFrom(
        this.attachedComponent.pipe(
          map((c) => c as C),
          take(1),
          timeout(RENDER_TIMEOUT),
          catchError((err) => {
            // 附加组件失败，几乎不可能出现，除非组件构造函数异常
            logger.warn('attach component catchError', err);
            return of(undefined);
          })
        )
      );
      if (!attachedComponent) {
        return undefined;
      }
      initialize?.(attachedComponent);
      const success = await lastValueFrom(
        attachedComponent.viewReady.pipe(
          filter((ready) => ready !== undefined),
          map((ready) => ready!.success),
          take(1),
          timeout(RENDER_TIMEOUT),
          catchError((err) => {
            // 组件没有正确处理 ViewReady 时间，或者超时
            logger.warn('viewReady catchError', err);
            return of(false);
          })
        )
      );
      if (!success) {
        // 加载失败
        logger.warn('Component viewReady failed');
        return undefined;
      }
      let sharePortal = document.getElementById('share-portal')!;
      if (domSelector) {
        const domElement = document.querySelector<HTMLElement>(domSelector);
        sharePortal = domElement || sharePortal;
      }
      const dpr = 1.5;
      // 多做一次，不然iOS safari 第一次会显示不了图片
      // await domtoimage.toPng(sharePortal, {
      //   quality: 0.9,
      //   width: sharePortal.clientWidth * dpr,
      //   height: sharePortal.clientHeight * dpr,
      //   style: {
      //     transform: `scale(${dpr})`,
      //     transformOrigin: 'top left',
      //   },
      // });
      const base64 = await domtoimage.toPng(sharePortal, {
        quality: 0.9,
        width: sharePortal.clientWidth * dpr,
        height: sharePortal.clientHeight * dpr,
        style: {
          transform: `scale(${dpr})`,
          transformOrigin: 'top left',
        },
      });
      logger.info('Share Image Base64:', base64);
      return base64;
    } finally {
      this.portal.next(undefined);
    }
  }
}
